/**
 * CampusMap.jsx
 * Main campus map view with building markers and status visualization
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItemButton,
  ListItemText
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useMapState } from '../hooks/useMapState';
import { useMapPolling } from '../hooks/useMapPolling';
import { getStatusByAvailability } from '../utils/statusColorMap';
import { CAMPUS_CONFIG } from '../utils/mapConfig';
import BuildingDetailPanel from '../components/BuildingDetailPanel';
import BuildingUserDetailPanel from '../components/BuildingUserDetailPanel';
import BuildingAlarmDetailPanel from '../components/BuildingAlarmDetailPanel';

const SYRACUSE_BUILDING_LABEL_MIN_ZOOM = 16.25;
const SYRACUSE_REGION_LABEL_MIN_ZOOM = 11.25;
const T_MOBILE_IPSEC_FOA_LABEL_MIN_ZOOM = 6.25;
const JMA_HEADQUARTERS_LABEL_MIN_ZOOM = 10;
const SYRACUSE_CAMPUS_CENTER = [-76.134, 43.038];
const SYRACUSE_REGIONS = [
  {
    displayName: 'North (Main) Campus',
    coordinates: [-76.13424557362573, 43.03763956649073]
  },
  {
    displayName: 'South Campus',
    coordinates: [-76.12043768192952, 43.01920207327971]
  }
];
const SYRACUSE_AREA_BOUNDS = {
  west: -76.25,
  east: -76.0,
  south: 42.95,
  north: 43.15
};

const getLabelCategory = (name, lng, lat) => {
  const normalizedName = name.toLowerCase().replace(/[^a-z0-9]/g, '');

  if (
    normalizedName.includes('tmobile') &&
    (normalizedName.includes('ipsec') || normalizedName.includes('foa'))
  ) {
    return 'tmobileIpsecFoa';
  }

  if (
    normalizedName.includes('jma') &&
    (normalizedName.includes('headquarters') || normalizedName.includes('hq'))
  ) {
    return 'jmaHeadquarters';
  }

  const isInSyracuseArea = (
  lng >= SYRACUSE_AREA_BOUNDS.west &&
  lng <= SYRACUSE_AREA_BOUNDS.east &&
  lat >= SYRACUSE_AREA_BOUNDS.south &&
  lat <= SYRACUSE_AREA_BOUNDS.north
  );

  return isInSyracuseArea ? 'syracuseCampus' : 'remote';
};

const getDisplayName = (name, labelCategory) => {
  if (labelCategory === 'tmobileIpsecFoa') return 'T-Mobile IPSEC FOA';
  if (labelCategory === 'jmaHeadquarters') return 'JMA Headquarters';
  return name;
};

const CampusMap = () => {
  const mapContainer = useRef(null);
  const map = useRef(null);

  const mapState = useMapState();
  const { buildings, statuses, selectedBuildingId } = mapState;

  const {
    startPolling,
    stopPolling,
    refresh
  } = useMapPolling(mapState);

  // eslint-disable-next-line no-unused-vars
  const [detailedBuilding, setDetailedBuilding] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [detailLoading, setDetailLoading] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showAlarmDetails, setShowAlarmDetails] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Keep a stable ref to mapState so effects don't re-run when it changes identity
  const mapStateRef = useRef(mapState);
  mapStateRef.current = mapState;

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current) return;

    // Only initialize if map doesn't exist
    if (map.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: CAMPUS_CONFIG.style,
      center: CAMPUS_CONFIG.center,
      zoom: CAMPUS_CONFIG.defaultZoom,
      minZoom: CAMPUS_CONFIG.minZoom,
      maxZoom: CAMPUS_CONFIG.maxZoom,
      pitch: 0,
      bearing: 0,
      pitchWithRotate: false,
      dragRotate: false,
      touchPitch: false
    });

    // Disable rotation gestures (keep map strictly 2D / north-up)
    map.current.dragRotate.disable();
    map.current.touchZoomRotate.disableRotation();
    map.current.keyboard.disableRotation?.();

    map.current.on('load', () => {
      console.log('Map loaded');
      setMapReady(true);
      // Ensure canvas matches container size after layout settles
      map.current.resize();

      // DEBUG: expose map instance and log zoom level on change
      window._campusMap = map.current;
      console.log(`Current zoom: ${map.current.getZoom().toFixed(2)}`);
      map.current.on('zoomend', () => {
        console.log(`Current zoom: ${map.current.getZoom().toFixed(2)}`);
      });

      // Fetch initial building data and start polling
      if (refresh) refresh();
      if (startPolling) startPolling();
      
      // Ensure markers are rendered after map is fully loaded
      // This will trigger the marker useEffect
    });

    // Keep map sized to its container on window/layout changes
    const handleResize = () => {
      if (map.current) map.current.resize();
    };
    window.addEventListener('resize', handleResize);

    // Observe container size changes (drawer toggle, layout reflow)
    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined' && mapContainer.current) {
      resizeObserver = new ResizeObserver(() => {
        if (map.current) map.current.resize();
      });
      resizeObserver.observe(mapContainer.current);
    }

    return () => {
      // Cleanup: stop polling and destroy map
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) resizeObserver.disconnect();
      if (stopPolling) stopPolling();
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- must run once on mount; refresh/startPolling/stopPolling identity is unstable and including them causes map re-init loops (see repo memory)
  }, []); // Empty deps - initialize once

  // Initialize map layers (runs once when map is ready)
  useEffect(() => {
    if (!map.current || !mapReady) return;

    // Check if layer already exists
    if (map.current.getLayer('buildings-layer')) {
      return;
    }

    console.log('Initializing buildings layer...');

    // Add GeoJSON source with empty data initially
    map.current.addSource('buildings', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });

    map.current.addSource('syracuse-campus-label', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: SYRACUSE_CAMPUS_CENTER
        },
        properties: {
          displayName: 'Syracuse University Campus'
        }
      }
    });

    map.current.addSource('syracuse-region-labels', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: SYRACUSE_REGIONS.map((region) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: region.coordinates
          },
          properties: {
            displayName: region.displayName
          }
        }))
      }
    });

    // Add circle layer for building markers
    map.current.addLayer({
      id: 'buildings-layer',
      type: 'circle',
      source: 'buildings',
      paint: {
        'circle-radius': 8,
        'circle-color': ['get', 'color'],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
        'circle-opacity': 0.9,
        'circle-stroke-opacity': 1
      }
    }); // Added on top of all layers

    // Add user-count circles underneath markers
    map.current.addLayer({
      id: 'user-heatmap-layer',
      type: 'circle',
      source: 'buildings',
      paint: {
        // Scales from 10px (1 user) to 90px (50+ users, the expected per-building high).
        'circle-radius': [
          'case',
          ['<=', ['to-number', ['get', 'userCount'], 0], 0],
          0,
          [
            'interpolate',
            ['linear'],
            ['min', ['to-number', ['get', 'userCount'], 0], 50],
            1,
            10,
            50,
            90
          ]
        ],
        'circle-color': '#0D2F66',
        'circle-opacity': [
          'case',
          ['<=', ['to-number', ['get', 'userCount'], 0], 0],
          0,
          [
            'interpolate',
            ['linear'],
            ['min', ['to-number', ['get', 'userCount'], 0], 50],
            1,
            0.75,
            50,
            0.25
          ]
        ],
        'circle-stroke-width': [
          'case',
          ['<=', ['to-number', ['get', 'userCount'], 0], 0],
          0,
          1.2
        ],
        'circle-stroke-color': '#0A2756',
        'circle-stroke-opacity': [
          'case',
          ['<=', ['to-number', ['get', 'userCount'], 0], 0],
          0,
          0.5
        ]
      }
    }, 'buildings-layer');

    // Keep labels for geographically separate sites visible at every zoom.
    map.current.addLayer({
      id: 'buildings-labels',
      type: 'symbol',
      source: 'buildings',
      filter: ['==', ['get', 'labelCategory'], 'remote'],
      layout: {
        'text-field': ['get', 'displayName'],
        'text-font': ['Open Sans Bold'],
        'text-size': 12,
        'text-offset': [0, 1.2],
        'text-anchor': 'top',
        'text-allow-overlap': false,
        'text-padding': 4,
        'symbol-sort-key': ['-', 0, ['to-number', ['get', 'userCount'], 0]]
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.8)',
        'text-halo-width': 1.5
      }
    });

    map.current.addLayer({
      id: 'tmobile-ipsec-foa-label',
      type: 'symbol',
      source: 'buildings',
      minzoom: T_MOBILE_IPSEC_FOA_LABEL_MIN_ZOOM,
      filter: ['==', ['get', 'labelCategory'], 'tmobileIpsecFoa'],
      layout: {
        'text-field': ['get', 'displayName'],
        'text-font': ['Open Sans Bold'],
        'text-size': 12,
        'text-offset': [0, 1.2],
        'text-anchor': 'top',
        'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
        'text-radial-offset': 1,
        'text-allow-overlap': false,
        'text-padding': 4
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.8)',
        'text-halo-width': 1.5
      }
    });

    map.current.addLayer({
      id: 'jma-headquarters-label',
      type: 'symbol',
      source: 'buildings',
      minzoom: JMA_HEADQUARTERS_LABEL_MIN_ZOOM,
      filter: ['==', ['get', 'labelCategory'], 'jmaHeadquarters'],
      layout: {
        'text-field': ['get', 'displayName'],
        'text-font': ['Open Sans Bold'],
        'text-size': 12,
        'text-offset': [0, 1.2],
        'text-anchor': 'top',
        'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
        'text-radial-offset': 1,
        'text-allow-overlap': false,
        'text-padding': 4
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.8)',
        'text-halo-width': 1.5
      }
    });

    map.current.addLayer({
      id: 'syracuse-building-labels',
      type: 'symbol',
      source: 'buildings',
      minzoom: SYRACUSE_BUILDING_LABEL_MIN_ZOOM,
      filter: ['==', ['get', 'labelCategory'], 'syracuseCampus'],
      layout: {
        'text-field': ['get', 'displayName'],
        'text-font': ['Open Sans Bold'],
        'text-size': 12,
        'text-offset': [0, 1.2],
        'text-anchor': 'top',
        'text-allow-overlap': false,
        'text-padding': 4,
        'symbol-sort-key': ['-', 0, ['to-number', ['get', 'userCount'], 0]]
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.8)',
        'text-halo-width': 1.5
      }
    });

    map.current.addLayer({
      id: 'syracuse-campus-label',
      type: 'symbol',
      source: 'syracuse-campus-label',
      maxzoom: SYRACUSE_REGION_LABEL_MIN_ZOOM,
      layout: {
        'text-field': ['get', 'displayName'],
        'text-font': ['Open Sans Bold'],
        'text-size': 14,
        'text-allow-overlap': true
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.8)',
        'text-halo-width': 1.5
      }
    });

    map.current.addLayer({
      id: 'syracuse-region-labels',
      type: 'symbol',
      source: 'syracuse-region-labels',
      minzoom: SYRACUSE_REGION_LABEL_MIN_ZOOM,
      maxzoom: SYRACUSE_BUILDING_LABEL_MIN_ZOOM,
      layout: {
        'text-field': ['get', 'displayName'],
        'text-font': ['Open Sans Bold'],
        'text-size': 14,
        'text-allow-overlap': false,
        'text-padding': 8
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.8)',
        'text-halo-width': 1.5
      }
    });

    // Use styleimagemissing to provide canvas-drawn icons on demand
    map.current.on('styleimagemissing', (e) => {
      const id = e.id;
      if (id !== 'power-alarm-icon' && id !== 'temp-alarm-icon') return;

      const size = 32;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');

      // Transparent background - icon only
      ctx.clearRect(0, 0, size, size);

      if (id === 'power-alarm-icon') {
        // Lightning bolt with dark outline for visibility on satellite
        const sc = 28 / 64;
        const ox = (size - 64 * sc) / 2;
        const oy = (size - 64 * sc) / 2;
        ctx.strokeStyle = 'rgba(0,0,0,0.7)';
        ctx.lineWidth = 2;
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(ox + 35 * sc, oy + 4 * sc);
        ctx.lineTo(ox + 15 * sc, oy + 31 * sc);
        ctx.lineTo(ox + 30 * sc, oy + 31 * sc);
        ctx.lineTo(ox + 24 * sc, oy + 60 * sc);
        ctx.lineTo(ox + 49 * sc, oy + 23 * sc);
        ctx.lineTo(ox + 35 * sc, oy + 23 * sc);
        ctx.closePath();
        ctx.stroke();
        ctx.fillStyle = '#F59E0B';
        ctx.fill();
      } else {
        // Thermometer: stem + bulb with dark outline
        const cx = size / 2;
        const stemW = size * 0.22;
        const stemTop = size * 0.05;
        const stemBot = size * 0.62;
        const bulbR = size * 0.26;
        const bulbCY = size * 0.74;

        ctx.strokeStyle = 'rgba(0,0,0,0.7)';
        ctx.lineWidth = 1.5;

        // Bulb
        ctx.beginPath();
        ctx.arc(cx, bulbCY, bulbR, 0, Math.PI * 2);
        ctx.fillStyle = '#EF4444';
        ctx.fill();
        ctx.stroke();

        // Stem
        ctx.beginPath();
        ctx.roundRect(cx - stemW / 2, stemTop, stemW, stemBot - stemTop, stemW / 2);
        ctx.fillStyle = '#EF4444';
        ctx.fill();
        ctx.stroke();
      }

      const imageData = ctx.getImageData(0, 0, size, size);
      map.current.addImage(id, { width: size, height: size, data: imageData.data });
    });

    // Add power alarm icon layer
    if (!map.current.getLayer('power-alarm-icons-layer')) {
      map.current.addLayer({
        id: 'power-alarm-icons-layer',
        type: 'symbol',
        source: 'buildings',
        filter: ['>', ['to-number', ['get', 'powerAlarm'], 0], 0],
        layout: {
          'icon-image': 'power-alarm-icon',
          'icon-size': 0.75,
          'icon-offset': [14, -14],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        }
      });
    }

    // Add temp alarm icon layer
    if (!map.current.getLayer('temp-alarm-icons-layer')) {
      map.current.addLayer({
        id: 'temp-alarm-icons-layer',
        type: 'symbol',
        source: 'buildings',
        filter: ['>', ['to-number', ['get', 'tempAlarm'], 0], 0],
        layout: {
          'icon-image': 'temp-alarm-icon',
          'icon-size': 0.75,
          'icon-offset': [-14, -14],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        }
      });
    }

    // Add hover effect
    map.current.on('mouseenter', 'buildings-layer', () => {
      map.current.getCanvas().style.cursor = 'pointer';
    });

    map.current.on('mouseleave', 'buildings-layer', () => {
      map.current.getCanvas().style.cursor = '';
    });

    // Handle clicks on buildings layer
    map.current.on('click', 'buildings-layer', (e) => {
      if (e.features.length > 0) {
        const feature = e.features[0];
        const building = mapStateRef.current.getBuilding(feature.properties.id);
        if (building) {
          mapStateRef.current.selectBuilding(building.id);
          setDetailedBuilding(building);
          setShowUserDetails(false);
          setShowAlarmDetails(false);
        }
      }
    });





    return () => {
      // Cleanup event listeners only
      if (map.current) {
        map.current.off('mouseenter', 'buildings-layer');
        map.current.off('mouseleave', 'buildings-layer');
        map.current.off('click', 'buildings-layer');
      }
    };
  }, [mapReady]);

  // Update marker data when buildings or statuses change
  useEffect(() => {
    if (!map.current || !mapReady || buildings.length === 0) return;

    const source = map.current.getSource('buildings');
    if (!source) return; // Layer not yet initialized

    // Find max user count for normalization
    const userCountsArray = buildings.map(b => statuses[b.id]?.userCount ?? b.userCount ?? 0);
    const maxUserCount = Math.max(...userCountsArray, 1);

    // Create GeoJSON feature collection from buildings
    const buildingsGeoJSON = {
      type: 'FeatureCollection',
      features: buildings.map((building) => {
        const statusData = statuses[building.id];
        const availability = statusData?.availability || building.availability;
        const userCount = statusData?.userCount ?? building.userCount ?? 0;
        const userNormalized = maxUserCount > 0 ? userCount / maxUserCount : 0;
        const statusInfo = getStatusByAvailability(availability);
        const tempAlarm = Number(statusData?.tempAlarm ?? building.tempAlarm ?? 0);
        const powerAlarm = Number(statusData?.powerAlarm ?? building.powerAlarm ?? 0);
        
        const labelCategory = getLabelCategory(building.name, building.lng, building.lat);
        const displayName = getDisplayName(building.name, labelCategory);

        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [building.lng, building.lat]
          },
          properties: {
            id: building.id,
            name: building.name,
            displayName: displayName,
            availability: availability,
            color: statusInfo.color.hex,
            userCount: userCount,
            userNormalized: userNormalized,
            tempAlarm: tempAlarm,
            powerAlarm: powerAlarm,
            labelCategory: labelCategory,
            status: statusInfo.status
          }
        };
      })
    };

    // Update source data (not recreate the layer)
    source.setData(buildingsGeoJSON);
    console.log(`Updated ${buildings.length} markers via GeoJSON layer`);
  }, [buildings, statuses, mapReady]);

  // Handle deselection
  useEffect(() => {
    if (!selectedBuildingId) {
      setDetailedBuilding(null);
      setShowUserDetails(false);
      setShowAlarmDetails(false);
    }
  }, [selectedBuildingId]);

  const searchResults = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return [];

    const startsWithMatches = buildings.filter((building) =>
      String(building.name || '').toLowerCase().startsWith(query)
    );

    const includesMatches = buildings.filter((building) => {
      const name = String(building.name || '').toLowerCase();
      return !name.startsWith(query) && name.includes(query);
    });

    return [...startsWithMatches, ...includesMatches].slice(0, 12);
  }, [buildings, searchText]);

  const focusBuilding = (building) => {
    if (!building || !map.current) return;

    map.current.flyTo({
      center: [building.lng, building.lat],
      zoom: Math.max(17, map.current.getZoom()),
      speed: 0.8,
      curve: 1.2,
      essential: true
    });

    mapState.selectBuilding(building.id);
    setDetailedBuilding(building);
    setShowUserDetails(false);
    setShowAlarmDetails(false);
    setSearchText(building.name || '');
    setShowSearchResults(false);
  };

  const selectedBuilding = mapState.getSelectedBuilding();
  const selectedStatus = selectedBuildingId ? statuses[selectedBuildingId] : null;
  const selectedMetrics = {
    attCurUsers: selectedStatus?.attCurUsers ?? selectedBuilding?.attCurUsers,
    attCurRx: selectedStatus?.attCurRx ?? selectedBuilding?.attCurRx,
    attCurTx: selectedStatus?.attCurTx ?? selectedBuilding?.attCurTx,
    attHistUsers: selectedStatus?.attHistUsers ?? selectedBuilding?.attHistUsers,
    attHistRx: selectedStatus?.attHistRx ?? selectedBuilding?.attHistRx,
    attHistTx: selectedStatus?.attHistTx ?? selectedBuilding?.attHistTx,
    tmoCurUsers: selectedStatus?.tmoCurUsers ?? selectedBuilding?.tmoCurUsers,
    tmoCurRx: selectedStatus?.tmoCurRx ?? selectedBuilding?.tmoCurRx,
    tmoCurTx: selectedStatus?.tmoCurTx ?? selectedBuilding?.tmoCurTx,
    tmoHistUsers: selectedStatus?.tmoHistUsers ?? selectedBuilding?.tmoHistUsers,
    tmoHistRx: selectedStatus?.tmoHistRx ?? selectedBuilding?.tmoHistRx,
    tmoHistTx: selectedStatus?.tmoHistTx ?? selectedBuilding?.tmoHistTx,
    totalCurUsers: selectedStatus?.totalCurUsers ?? selectedBuilding?.totalCurUsers,
    totalCurRx: selectedStatus?.totalCurRx ?? selectedBuilding?.totalCurRx,
    totalCurTx: selectedStatus?.totalCurTx ?? selectedBuilding?.totalCurTx,
    totalHistUsers: selectedStatus?.totalHistUsers ?? selectedBuilding?.totalHistUsers,
    totalHistRx: selectedStatus?.totalHistRx ?? selectedBuilding?.totalHistRx,
    totalHistTx: selectedStatus?.totalHistTx ?? selectedBuilding?.totalHistTx
  };
  const selectedAlarms = {
    tempAlarm: selectedStatus?.tempAlarm ?? selectedBuilding?.tempAlarm,
    powerAlarm: selectedStatus?.powerAlarm ?? selectedBuilding?.powerAlarm,
    oamAlarm: selectedStatus?.oamAlarm ?? selectedBuilding?.oamAlarm,
    serviceAlarm: selectedStatus?.serviceAlarm ?? selectedBuilding?.serviceAlarm
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', height: 'calc(100vh - 64px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Map Container */}
      <Box
        ref={mapContainer}
        sx={{
          width: '100%',
          flex: 1,
          position: 'relative',
          minHeight: 0
        }}
      />

      {/* Site Search */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 120,
          width: { xs: 'calc(100% - 32px)', sm: 380 }
        }}
      >
        <TextField
          fullWidth
          size="small"
          value={searchText}
          placeholder="Search site name"
          onFocus={() => {
            if (searchText.trim()) setShowSearchResults(true);
          }}
          onChange={(event) => {
            const value = event.target.value;
            setSearchText(value);
            setShowSearchResults(Boolean(value.trim()));
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && searchResults.length > 0) {
              event.preventDefault();
              focusBuilding(searchResults[0]);
            }
            if (event.key === 'Escape') {
              setShowSearchResults(false);
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            sx: {
              backgroundColor: '#fff',
              borderRadius: 1
            }
          }}
        />

        {showSearchResults && (
          <Paper
            elevation={6}
            sx={{
              mt: 1,
              maxHeight: 300,
              overflowY: 'auto'
            }}
          >
            {searchResults.length > 0 ? (
              <List dense sx={{ py: 0 }}>
                {searchResults.map((building) => (
                  <ListItemButton
                    key={building.id}
                    onClick={() => focusBuilding(building)}
                  >
                    <ListItemText
                      primary={building.name}
                      secondary={`${building.lat.toFixed(4)}, ${building.lng.toFixed(4)}`}
                    />
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <Typography sx={{ p: 1.5, color: '#666', fontSize: '0.9rem' }}>
                No matching sites found.
              </Typography>
            )}
          </Paper>
        )}
      </Box>

      {/* Building Detail Panel */}
      {selectedBuilding && (
        <BuildingDetailPanel
          building={selectedBuilding}
          status={selectedStatus}
          isLoading={detailLoading}
          onUsersClick={() => {
            setShowUserDetails(true);
            setShowAlarmDetails(false);
          }}
          onAlarmsClick={() => {
            setShowAlarmDetails(true);
            setShowUserDetails(false);
          }}
          onClose={() => {
            mapState.deselectBuilding();
            setDetailedBuilding(null);
            setShowUserDetails(false);
            setShowAlarmDetails(false);
          }}
        />
      )}

      {selectedBuilding && showUserDetails && (
        <BuildingUserDetailPanel
          building={selectedBuilding}
          metrics={selectedMetrics}
          onClose={() => setShowUserDetails(false)}
        />
      )}

      {selectedBuilding && showAlarmDetails && (
        <BuildingAlarmDetailPanel
          building={selectedBuilding}
          alarms={selectedAlarms}
          onClose={() => setShowAlarmDetails(false)}
        />
      )}

      {/* No UI overlays - just the map and detail panel */}
    </Box>
  );
};

export default CampusMap;
